import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const KINDS = [
  { value: "PRODUTO", label: "Produto acabado" },
  { value: "FILAMENTO", label: "Filamento" },
  { value: "MATERIAL", label: "Material / insumo" },
  { value: "COMPONENTE", label: "Componente / peça" },
];

const TYPES = [
  { value: "ENTRADA", label: "Entrada (soma a quantidade)" },
  { value: "SAIDA", label: "Saída (subtrai a quantidade)" },
  { value: "AJUSTE", label: "Ajuste (define o estoque final)" },
];

/** Movimentação manual: estoque geral da empresa ou consignado de um parceiro. */
export function ManualMovementModal({ partner = null, preset = null, onClose, onSaved }) {
  const isPartner = !!partner;
  const [kind, setKind] = useState("PRODUTO");
  const [type, setType] = useState("ENTRADA");
  const [itemId, setItemId] = useState(preset?.product_id || "");
  const [variationId, setVariationId] = useState(preset?.variation_id || "");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    const calls = isPartner
      ? [api.get("/products"), api.get(`/partners/${partner.id}/stock`)]
      : [api.get("/products"), api.get("/filaments"), api.get("/materials")];
    Promise.all(calls)
      .then((res) =>
        setData(
          isPartner
            ? { products: res[0].data, consigned: res[1].data }
            : { products: res[0].data, filaments: res[1].data, materials: res[2].data }
        )
      )
      .catch((e) => toast.error(apiError(e)));
  }, [isPartner, partner]);

  useEffect(() => {
    if (isPartner || preset) return;
    setItemId("");
    setVariationId("");
  }, [kind, isPartner, preset]);

  const [plates, setPlates] = useState([]);
  useEffect(() => {
    if (kind !== "COMPONENTE" || isPartner) return;
    api
      .get("/plates")
      .then(({ data: d }) => setPlates(d.filter((p) => p.plate_type === "COMPONENTE")))
      .catch(() => setPlates([]));
  }, [kind, isPartner]);

  const items = useMemo(() => {
    if (!data) return [];
    if (isPartner || kind === "PRODUTO") {
      return (data.products || []).map((p) => ({ value: p.id, label: `${p.code || ""} ${p.name}`.trim() }));
    }
    if (kind === "FILAMENTO") {
      return (data.filaments || []).map((f) => ({
        value: f.id,
        label: `${f.material} ${f.color_name}${f.brand ? ` · ${f.brand}` : ""}`,
      }));
    }
    if (kind === "MATERIAL") {
      return (data.materials || []).map((m) => ({ value: m.id, label: m.name }));
    }
    return plates.map((p) => ({ value: p.id, label: `${p.name} · ${p.product_name || ""}`.trim() }));
  }, [data, kind, isPartner, plates]);

  const product = (data?.products || []).find((p) => p.id === itemId);
  const variations =
    (isPartner || kind === "PRODUTO") && product
      ? (product.variations || []).filter((v) => v.active !== false)
      : [];

  const current = useMemo(() => {
    if (!data) return 0;
    if (isPartner) {
      const key = variationId ? `${itemId}::${variationId}` : itemId;
      const row = (data.consigned || []).find((r) => (r.key || r.product_id) === key);
      return Number(row?.quantity || 0);
    }
    if (kind === "PRODUTO") {
      if (variationId) {
        const v = (product?.variations || []).find((x) => x.id === variationId);
        return Number(v?.stock_units || 0);
      }
      return Number(product?.stock_units || 0);
    }
    if (kind === "FILAMENTO") {
      const f = (data.filaments || []).find((x) => x.id === itemId);
      return Number(f?.balance_g ?? 0);
    }
    if (kind === "MATERIAL") {
      const m = (data.materials || []).find((x) => x.id === itemId);
      return Number(m?.quantity ?? 0);
    }
    const pl = plates.find((x) => x.id === itemId);
    return Number(pl?.stock_units ?? 0);
  }, [data, isPartner, kind, itemId, variationId, product, plates]);

  const qty = Number(quantity || 0);
  const after =
    type === "AJUSTE" ? qty : type === "ENTRADA" ? current + qty : current - Math.abs(qty);
  const unit = !isPartner && kind === "FILAMENTO" ? "g" : "un";

  const submit = async (e) => {
    e.preventDefault();
    if (!itemId) return toast.error("Escolha o item que você quer movimentar.");
    if (variations.length && !variationId)
      return toast.error("Escolha a combinação que você quer movimentar.");
    if (type !== "AJUSTE" && !qty) return toast.error("Informe a quantidade.");
    if (notes.trim().length < 2) return toast.error("Escreva o motivo da movimentação.");
    if (after < 0) return toast.error(`Esta movimentação deixaria o saldo negativo (${after}).`);
    setBusy(true);
    try {
      const body = {
        type,
        quantity: Math.abs(qty),
        notes: notes.trim(),
        variation_id: variationId || null,
      };
      const { data: res } = isPartner
        ? await api.post(`/partners/${partner.id}/stock/manual-movements`, {
            ...body,
            product_id: itemId,
          })
        : await api.post("/stock/manual-movements", { ...body, kind, item_id: itemId });
      toast.success(
        `${res.item_name}: ${res.quantity_before} → ${res.quantity_after} (${
          res.quantity_moved > 0 ? "+" : ""
        }${res.quantity_moved})`
      );
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Movimentar estoque"
      description={
        isPartner
          ? `Corrige o consignado de ${partner.name}. Não gera reposição, venda nem financeiro.`
          : "Entrada, saída ou ajuste manual. Não gera produção, venda nem lançamento financeiro."
      }
      testid="manual-movement-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        {!isPartner && (
          <Field label="Tipo de item">
            <NativeSelect
              value={kind}
              onChange={setKind}
              options={KINDS}
              data-testid="manual-kind-select"
            />
          </Field>
        )}

        <Field label="Item">
          <NativeSelect
            value={itemId}
            onChange={(v) => {
              setItemId(v);
              setVariationId("");
            }}
            placeholder="Escolha o item"
            options={items}
            data-testid="manual-item-select"
          />
        </Field>

        {variations.length > 0 && (
          <Field label="Cor / combinação" hint="Só esta combinação será movimentada.">
            <NativeSelect
              value={variationId}
              onChange={setVariationId}
              placeholder="Escolha a combinação"
              options={variations.map((v) => ({ value: v.id, label: v.name }))}
              data-testid="manual-variation-select"
            />
          </Field>
        )}

        <Field label="Movimentação">
          <NativeSelect
            value={type}
            onChange={setType}
            options={TYPES}
            data-testid="manual-type-select"
          />
        </Field>

        <Field
          label={type === "AJUSTE" ? `Novo estoque desejado (${unit})` : `Quantidade (${unit})`}
          hint={
            type === "AJUSTE"
              ? "O sistema calcula sozinho a diferença para chegar neste saldo."
              : undefined
          }
        >
          <Input
            type="number"
            min="0"
            step={unit === "g" ? "1" : "0.01"}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="font-num"
            data-testid="manual-quantity-input"
          />
        </Field>

        <Field label="Motivo / observação">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Ex.: conferência física, perda, acerto inicial"
            data-testid="manual-notes-input"
          />
        </Field>

        {itemId && (
          <div
            className="rounded-lg border border-white/10 bg-secondary/40 p-3 text-sm"
            data-testid="manual-preview"
          >
            <p className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Estoque atual</span>
              <span className="font-num">
                {current.toLocaleString("pt-BR")} {unit}
              </span>
            </p>
            <p className="mt-1 flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Após a movimentação</span>
              <span
                className={`font-num ${after < 0 ? "text-destructive" : "text-brand"}`}
                data-testid="manual-preview-after"
              >
                {after.toLocaleString("pt-BR")} {unit}
              </span>
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="h-11 rounded-full px-5"
            data-testid="manual-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={busy}
            className="h-11 rounded-full px-6 font-semibold"
            data-testid="manual-confirm-btn"
          >
            {busy ? "Salvando..." : "Confirmar movimentação"}
          </Button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
